import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { AutoComplete } from "primeng/autocomplete";
import { FloatLabel } from "primeng/floatlabel";
import { FormControlBaseDirective } from "../form-control-base.directive";
import { TagsSelectOption } from "./tags-select-option.model";

let nextId = 0;

@Component({
    selector: "kno2-form-tags-select",
    imports: [FormsModule, AutoComplete, FloatLabel, NgTemplateOutlet],
    templateUrl: "./form-tags-select.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormTagsSelectComponent extends FormControlBaseDirective {
    public readonly options = input<TagsSelectOption[]>([]);
    public readonly inputId = input<string>(`kno2-form-tags-select-${nextId++}`);
    public readonly placeholder = input<string>("");
    public readonly tagsChange = output<string[]>();
    public readonly searchChange = output<string>();

    protected readonly tags = signal<string[]>([]);
    protected readonly labels = linkedSignal<TagsSelectOption[], Map<string, string>>({
        source: this.options,
        computation: (options, previous) =>
            new Map([...(previous?.value ?? []), ...options.map(({ value, label }): [string, string] => [value, label ?? value])])
    });
    protected readonly filteredOptions = computed(() => {
        const query = this.query();
        if (query === null) return [];

        const search = query.toLowerCase();
        const selected = new Set(this.tags());
        return this.options()
            .filter(({ value, label }) => !selected.has(value) && (label ?? value).toLowerCase().includes(search))
            .map(({ value }) => value);
    });

    private readonly query = signal<string | null>(null);

    private blurTimeout: ReturnType<typeof setTimeout> | undefined;

    protected applyValue(value: unknown): void {
        const tags = value as string | string[];
        this.tags.set(Array.isArray(tags) ? tags : tags ? [tags] : []);
    }

    protected setTags(tags: string[]): void {
        this.tags.set(tags);
        this.emitChange(tags);
        this.tagsChange.emit(tags);
    }

    protected filter(query: string): void {
        this.query.set(query ?? "");
        this.searchChange.emit(query ?? "");
    }

    protected selectFirst(event: Event): void {
        event.preventDefault();
        const target = event.target as HTMLInputElement;
        const value = target?.value?.trim();
        if (!value) return;

        const filtered = this.filteredOptions();
        const newItem = filtered.length ? filtered[0] : value;
        if (this.tags().includes(newItem)) return;

        this.setTags([...this.tags(), newItem]);
        target.value = "";
        this.query.set(null);
    }

    protected onBlur(event: Event): void {
        this.markTouched();
        clearTimeout(this.blurTimeout);
        this.blurTimeout = setTimeout(() => this.selectFirst(event), 100);
    }
}
